import { useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { PacksContext } from '@/components/app/layout/PacksProvider';
import { useProgramInfo } from '@/hooks/app/useProgramInfo';
import { useProgramState, useThemeState } from '@/hooks/store';
import { setTheme } from '@/store/theme/actions';

export function useInitializeProgram(): void {
	const program = useProgramState();
	const theme = useThemeState();
	const dispatch = useDispatch();
	const { setPageTitle, setPageSubtitle } = useContext(PacksContext);
	const { backgroundImage } = useProgramInfo();

	useEffect(() => {
		setPageTitle(null);
		// PacksProvider is mounted app-wide, so a subtitle set by a previously-visited
		// feed/class page (via setPageSubtitle) would otherwise persist into this page's
		// PacksList render.
		setPageSubtitle(null);

		// All programs share one route, so this component instance is reused when the id changes —
		// a one-shot guard would leave the previous program's backdrop on screen.
		// The comparison keeps that safe: `theme` is both a dependency here and part of the
		// payload, so an unconditional dispatch would re-enter this effect for as long as the
		// payload keeps differing — today the assets are stable store references, but comparing
		// makes that independent of asset-object identity.
		if (program && theme.webAppBackgroundImage?.url !== backgroundImage?.url) {
			dispatch(
				setTheme({
					...theme,
					webAppBackgroundImage: backgroundImage,
				}),
			);
		}
	}, [backgroundImage, dispatch, program, setPageSubtitle, setPageTitle, theme]);
}
